import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSession } from '../lib/session';
import { api } from '../lib/api';
import { Avatar, Button } from './ui';

/**
 * Both shells read the same role nav from the design kit.
 *
 * This file used to keep its own copy, so the same four destinations were called "Work" and
 * "Plans" here and "Learning events" and "Learning plans" on every other screen. One list,
 * one set of names, wherever a teacher happens to be standing.
 */
import { RoleNav } from '../design/kit';

export function Shell({
  title,
  subtitle,
  actions,
  children,
  width = 'default',
  crumbs,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  width?: 'default' | 'narrow' | 'wide';
  crumbs?: ReactNode;
}) {
  const { user, school, signOut } = useSession();
  const navigate = useNavigate();

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="sidebar__brand">
          <span className="sidebar__mark" style={school?.colorPrimary ? { background: school.colorPrimary } : undefined}>
            {school?.logoUrl ? <img src={school.logoUrl} alt="" /> : (school?.name?.[0] ?? 'V')}
          </span>
          <span className="truncate">{school?.name ?? 'SchoolVerse'}</span>
        </div>

        <RoleNav />

        <div className="sidebar__foot">
          <Avatar initials={user?.avatarInitials ?? '?'} size="sm" />
          <span className="truncate" style={{ flex: 1 }}>{user?.name}</span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ color: 'inherit' }}
            onClick={() => {
              signOut();
              navigate('/signin');
            }}
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="header">
          <div style={{ minWidth: 0 }}>
            {crumbs}
            <h1 className="header__title">{title}</h1>
            {subtitle && <div className="header__sub">{subtitle}</div>}
          </div>
          <div className="header__actions">
            {user?.role !== 'student' && <SearchField />}
            {actions}
          </div>
        </header>
        <div className={`content ${width === 'narrow' ? 'content--narrow' : width === 'wide' ? 'content--wide' : ''}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

interface SearchGroup {
  label: string;
  items: { id: string; title: string; subtitle?: string; href: string }[];
}

/** 12e — the same field on every list, scoped by role on the server. */
function SearchField() {
  const [q, setQ] = useState('');
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setGroups([]);
      return;
    }
    // Debounced so a fast typist does not fire a request per keystroke.
    const t = setTimeout(() => {
      api
        .get<{ groups: SearchGroup[] }>(`/search?q=${encodeURIComponent(q)}`)
        .then((res) => {
          setGroups(res.groups);
          setOpen(true);
        })
        .catch(() => setGroups([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <input
        className="input"
        style={{ minWidth: 220 }}
        type="search"
        value={q}
        placeholder="Search"
        aria-label="Search"
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => groups.length > 0 && setOpen(true)}
      />
      {open && q.trim().length >= 2 && (
        <div
          className="card"
          style={{ position: 'absolute', top: '100%', right: 0, marginTop: 6, width: 340, zIndex: 50, boxShadow: 'var(--shadow)', padding: 8 }}
        >
          {groups.length === 0 ? (
            <div className="small muted" style={{ padding: 12 }}>
              Nothing matches “{q}”.
            </div>
          ) : (
            groups.map((g) => (
              <div key={g.label}>
                <div className="section-label" style={{ padding: '8px 10px 4px' }}>{g.label}</div>
                {g.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="btn btn--ghost"
                    style={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left' }}
                    onClick={() => {
                      setOpen(false);
                      setQ('');
                      navigate(item.href);
                    }}
                  >
                    <span className="truncate">{item.title}</span>
                    {item.subtitle && <span className="micro muted">{item.subtitle}</span>}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/** A bare page for screens with no shell: sign-in, join, projector, print. */
export function Plain({ children, tone = 'canvas' }: { children: ReactNode; tone?: 'canvas' | 'app' | 'dark' }) {
  const background = tone === 'dark' ? 'var(--c-dark)' : tone === 'app' ? 'var(--c-canvas)' : 'var(--c-canvas)';
  return <div style={{ minHeight: '100vh', background, color: tone === 'dark' ? '#fff' : undefined }}>{children}</div>;
}

export { Button };
